import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaShoppingCart } from 'react-icons/fa';
import { Clock, Utensils } from 'lucide-react';
import { RatingStarsFa, getDisplayRating } from '../../utils/ratingStars';
import namkeFallback from '../../assets/namke-fallback.png';
import usePromotedMenuIds from '../../hooks/usePromotedMenuIds';

const MenuTabs = ({
  activeTab,
  setActiveTab,
  handleDishClick,
  handleMenuClick,
  dishes = [],
  menus = [],
  categories = [],
  getDishesByCategory,
  getCategoryName,
  formatPrice,
  getImageUrl,
  handleAddToCart
}) => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [filteredDishes, setFilteredDishes] = useState([]);
  const restaurantId = menus?.[0]?.restaurant_id ?? dishes?.[0]?.restaurant_id ?? null;
  const promotedMenuIds = usePromotedMenuIds(restaurantId);

  // Filtrer les plats par catégorie
  useEffect(() => {
    if (getDishesByCategory) {
      const filtered = getDishesByCategory(selectedCategory);
      setFilteredDishes(filtered);
    } else {
      // Fallback si la fonction n'est pas fournie
      let filtered = dishes;
      if (selectedCategory !== 'all') {
        filtered = dishes.filter(dish => {
          if (typeof dish.category === 'object' && dish.category !== null) {
            return dish.category.id === selectedCategory;
          }
          return dish.category === selectedCategory;
        });
      }
      setFilteredDishes(filtered);
    }
  }, [dishes, selectedCategory, getDishesByCategory]);

  // Rendre un plat — carte liste horizontale, calquée sur la maquette Namke Cuisinier Detail
  const renderDish = (dish) => {
    const displayRating = getDisplayRating(dish, 0);
    return (
    <div
      key={dish.id}
      className="flex items-center gap-3 sm:gap-4 bg-namke-white border border-namke-black/10 cursor-pointer"
      style={{ borderRadius: '22px', padding: '16px', transition: 'box-shadow 160ms ease' }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 10px 22px rgba(5,16,4,0.08)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
      onClick={() => handleDishClick && handleDishClick(dish.id)}
    >
      <div className="flex-none overflow-hidden w-20 h-20 sm:w-[104px] sm:h-[104px]" style={{ borderRadius: '20px' }}>
        <img
          src={getImageUrl(dish.dish_image_url || dish.image_url)}
          alt={dish.name}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.src = namkeFallback;
          }}
        />
      </div>

      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <strong className="font-display text-namke-black truncate" style={{ fontSize: '20px', fontWeight: 600 }}>{dish.name}</strong>
          <span className="ml-auto flex items-center gap-1 text-namke-darkGreen font-sub whitespace-nowrap" style={{ fontSize: '13px', fontWeight: 600 }}>
            <RatingStarsFa rating={displayRating} size="sm" /> {t('restaurant.reviewCountParens', { count: dish.review_count ?? dish.reviews ?? 0 })}
          </span>
        </div>
        <p className="text-namke-black/70 truncate" style={{ fontSize: '14.5px', lineHeight: 1.5 }}>{dish.description}</p>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mt-1">
          <strong className="text-namke-black" style={{ fontSize: '16.5px' }}>
            {formatPrice ? formatPrice(dish.price) : `${dish.price}€`}
          </strong>
          <span className="flex items-center gap-1 bg-namke-cream text-namke-black/70 font-sub whitespace-nowrap" style={{ fontSize: '12.5px', padding: '6px 13px', borderRadius: '999px' }}>
            <Clock className="w-3 h-3" />{t('restaurant.minutes', { count: dish.preparation_time || 30 })}
          </span>
          <button
            onClick={(e) => { e.stopPropagation(); handleAddToCart && handleAddToCart(dish); }}
            className="ml-auto flex-none flex items-center justify-center bg-namke-terracotta text-namke-white hover:bg-[#CD3A16] transition-colors"
            style={{ width: '38px', height: '38px', borderRadius: '999px', fontSize: '19px', lineHeight: 1 }}
            title={t('home.addToCart')}
          >
            <FaShoppingCart className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
    );
  };

  // Rendre un menu
  const renderMenu = (menu) => {
    const displayRating = getDisplayRating(menu, 0);
    return (
    <div
      key={menu.id}
      className="bg-namke-white overflow-hidden cursor-pointer"
      style={{ borderRadius: '22px', boxShadow: '0 2px 10px rgba(5,16,4,0.05)', transition: 'box-shadow 160ms ease' }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 10px 22px rgba(5,16,4,0.08)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 2px 10px rgba(5,16,4,0.05)'; }}
      onClick={() => handleMenuClick && handleMenuClick(menu)}
    >
      {/* Image du menu */}
      <div className="h-48 relative">
        <img
          src={getImageUrl(menu.image_url || menu.dish_image_url || (menu.dishes && menu.dishes[0] && (menu.dishes[0].image_url || menu.dishes[0].dish_image_url)))}
          alt={menu.name}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.src = namkeFallback;
          }}
        />
        <div className="absolute top-3 left-3">
          <span className="text-namke-white font-sub" style={{ backgroundColor: '#054625', fontSize: '11.5px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '6px 13px', borderRadius: '999px' }}>
            {t('restaurant.menu')}
          </span>
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); handleMenuClick && handleMenuClick(menu); }}
          className="absolute top-3 right-3 flex items-center justify-center bg-namke-white hover:bg-namke-cream transition-colors"
          style={{ width: '38px', height: '38px', borderRadius: '999px' }}
          title={t('restaurant.viewDetail')}
        >
          <FaShoppingCart className="w-4 h-4" style={{ color: '#054625' }} />
        </button>
        {promotedMenuIds.has(Number(menu.id)) && (
          <div className="absolute bottom-3 left-3">
            <span className="text-namke-white font-sub" style={{ backgroundColor: '#EF4C23', fontSize: '11.5px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '6px 13px', borderRadius: '999px' }}>
              {t('home.promo')}
            </span>
          </div>
        )}
      </div>

      {/* Informations du menu */}
      <div className="p-4 flex flex-col gap-2">
        <div className="flex items-baseline gap-2">
          <strong className="font-display text-namke-black truncate" style={{ fontSize: '18px', fontWeight: 600 }}>{menu.name}</strong>
          <span className="ml-auto font-display text-namke-black whitespace-nowrap" style={{ fontSize: '18px', fontWeight: 900 }}>
            {formatPrice ? formatPrice(menu.price || 0) : `${menu.price || 0}€`}
          </span>
        </div>

        <p className="text-namke-black/65 line-clamp-2" style={{ fontSize: '13.5px', lineHeight: 1.5 }}>{menu.description}</p>

        <span className="text-namke-black/50 font-sub" style={{ fontSize: '12.5px' }}>
          {t('restaurant.dishesIncluded', { count: menu.dishes ? menu.dishes.length : 0 })}
        </span>

        {/* Plats inclus */}
        {menu.dishes && menu.dishes.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1">
            {menu.dishes.slice(0, 3).map((dish, index) => (
              <span key={index} className="text-namke-black/70 font-sub bg-namke-cream" style={{ fontSize: '12px', padding: '5px 11px', borderRadius: '999px' }}>
                {typeof dish === 'object' ? dish.name : dish}
              </span>
            ))}
            {menu.dishes.length > 3 && (
              <span className="text-namke-black/70 font-sub bg-namke-cream" style={{ fontSize: '12px', padding: '5px 11px', borderRadius: '999px' }}>
                {t('restaurant.moreCount', { count: menu.dishes.length - 3 })}
              </span>
            )}
          </div>
        )}

        <div className="flex items-center justify-between mt-1 pt-2" style={{ borderTop: '1px solid rgba(5,16,4,0.06)' }}>
          <span className="flex items-center gap-1">
            <RatingStarsFa rating={displayRating} size="sm" />
            <span className="text-namke-black/50 font-sub" style={{ fontSize: '12.5px' }}>
              {t('restaurant.reviewCountParens', { count: menu.review_count ?? menu.reviews ?? 0 })}
            </span>
          </span>
          <span className="font-sub font-semibold" style={{ fontSize: '13px', color: '#054625' }}>
            {t('restaurant.detailArrow')}
          </span>
        </div>
      </div>
    </div>
    );
  };

  return (
    <div className="mb-8">
      {/* Tabs */}
      <div className="flex gap-1 mb-6" style={{ background: '#FCFBF5', borderRadius: '999px', padding: '5px', width: 'fit-content' }}>
        <button
          onClick={() => setActiveTab('lunch')}
          className="font-sub font-semibold transition-colors"
          style={{
            fontSize: '14px', padding: '10px 20px', borderRadius: '999px',
            background: activeTab === 'lunch' ? '#054625' : 'transparent',
            color: activeTab === 'lunch' ? '#FBF9D9' : 'rgba(5,16,4,0.55)',
          }}
        >
          {t('restaurant.dishesTab', { count: dishes.length })}
        </button>
        <button
          onClick={() => setActiveTab('menus')}
          className="font-sub font-semibold transition-colors"
          style={{
            fontSize: '14px', padding: '10px 20px', borderRadius: '999px',
            background: activeTab === 'menus' ? '#054625' : 'transparent',
            color: activeTab === 'menus' ? '#FBF9D9' : 'rgba(5,16,4,0.55)',
          }}
        >
          {t('restaurant.menusTab', { count: menus.length })}
        </button>
      </div>

      {/* Filtres pour les plats */}
      {activeTab === 'lunch' && categories.length > 0 && (
        <div className="mb-6">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory('all')}
              className="font-sub font-medium transition-colors"
              style={{
                fontSize: '14px', padding: '9px 18px', borderRadius: '999px',
                background: selectedCategory === 'all' ? '#054625' : '#FCFBF5',
                color: selectedCategory === 'all' ? '#FBF9D9' : 'rgba(5,16,4,0.7)',
              }}
            >
              {t('restaurant.allCategories')}
            </button>
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className="font-sub font-medium transition-colors"
                style={{
                  fontSize: '14px', padding: '9px 18px', borderRadius: '999px',
                  background: selectedCategory === category.id ? '#054625' : '#FCFBF5',
                  color: selectedCategory === category.id ? '#FBF9D9' : 'rgba(5,16,4,0.7)',
                }}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Contenu des tabs */}
      {activeTab === 'lunch' ? (
        <div>
          {filteredDishes.length > 0 ? (
            <div className="flex flex-col gap-4">
              {filteredDishes.map(renderDish)}
            </div>
          ) : (
            <div className="text-center py-12">
              <Utensils className="w-14 h-14 mx-auto mb-4" style={{ color: 'rgba(5,16,4,0.18)' }} />
              <h3 className="font-display text-namke-black mb-2" style={{ fontSize: '18px', fontWeight: 600 }}>{t('restaurant.noDishFound')}</h3>
              <p className="text-namke-black/55" style={{ fontSize: '14px' }}>
                {selectedCategory === 'all'
                  ? t('restaurant.noDishesYet')
                  : t('restaurant.noDishInCategory')
                }
              </p>
            </div>
          )}
        </div>
      ) : (
        <div>
          {menus.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {menus.map(renderMenu)}
            </div>
          ) : (
            <div className="text-center py-12">
              <Utensils className="w-14 h-14 mx-auto mb-4" style={{ color: 'rgba(5,16,4,0.18)' }} />
              <h3 className="font-display text-namke-black mb-2" style={{ fontSize: '18px', fontWeight: 600 }}>{t('restaurant.noMenuFound')}</h3>
              <p className="text-namke-black/55" style={{ fontSize: '14px' }}>
                {t('restaurant.noMenusYet')}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MenuTabs;